import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Alan, Buton, Kart, Rozet } from "@koclukpanel/ui";
import { ApiHatasi, tekTabloOnizle, tekTabloUygula, type TabloOnizlemesi } from "./api";

/**
 * TEK TABLO KURTARMA — F-YED-28
 *
 * ================================================================
 * TEHLİKE GERİ GELENLER DEĞİL, KAYBOLANLAR
 * ================================================================
 * Tek KAYIT kurtarma (F-YED-27) bir satırı geri getirir; tek TABLO
 * kurtarma tabloyu yedekteki hâline DÖNDÜRÜR. Aradaki fark yıkıcı:
 * yedek alındıktan sonra eklenen her satır yedekte yok ve tabloyu
 * geri döndürmek onları SİLMEK demek.
 *
 * Ekran bu yüzden üç sayıyı ayrı gösteriyor ve "silinecek" olanı
 * ayrı bir tonla yazıyor. Tek bir "N satır etkilenecek" cümlesi,
 * kullanıcının onayladığı şeyin ne olduğunu gizlerdi.
 *
 * ================================================================
 * ÖNİZLEME HİÇBİR ŞEY YAZMAZ
 * ================================================================
 * Sunucu yedeği geçici bir veritabanına açıp karşılaştırıyor.
 * Yazma isteği ÖNİZLEMEDEN gelen `parmakIzi`yi geri gönderiyor:
 * arada eklenen bir satır parmak izini değiştirir ve yazma reddedilir.
 * Onay GÖRÜLEN özete ait olmalı.
 *
 * ================================================================
 * BAĞLANTI KIRILIYORSA DÜĞME YOK
 * ================================================================
 * Silinecek satırlara başka tablolardan referans varsa sunucu
 * `calistirilabilir: false` dönüyor. Veri kaybı kullanıcının bilerek
 * alabileceği bir karar; ama BAŞKA tabloların satırlarını sahipsiz
 * bırakmak farklı — o satırlar bu ekranda görünmüyor ve sahibi başka
 * biri olabilir.
 */

const RISK_TONU: Record<TabloOnizlemesi["risk"], "onay" | "uyari" | "hata"> = {
  GUVENLI: "onay",
  VERI_KAYBI: "uyari",
  BAGLANTI_KIRILIR: "hata",
};

const RISK_ADI: Record<TabloOnizlemesi["risk"], string> = {
  GUVENLI: "Güvenli",
  VERI_KAYBI: "Veri kaybı var",
  BAGLANTI_KIRILIR: "Bağlantı kırılıyor",
};

export function TekTabloKurtarma({ runId }: { runId: string }) {
  const [tablo, setTablo] = useState("");
  const [onizleme, setOnizleme] = useState<TabloOnizlemesi | null>(null);
  const [onay, setOnay] = useState("");
  const [sonuc, setSonuc] = useState<string | null>(null);

  const onizle = useMutation({
    mutationFn: () => tekTabloOnizle(runId, tablo.trim()),
    onSuccess: (v) => {
      setOnizleme(v);
      setOnay("");
      setSonuc(null);
    },
  });

  const uygula = useMutation({
    mutationFn: () =>
      tekTabloUygula({ runId, tablo: onizleme!.tablo, parmakIzi: onizleme!.parmakIzi }),
    onSuccess: (v) => {
      setSonuc(
        `${v.tablo}: ${v.eklenen} satır geri geldi, ${v.guncellenen} satır güncellendi, ${v.silinen} satır silindi.`,
      );
      // Önizleme TÜKETİLDİ: aynı parmak izi ikinci kez kullanılamaz ve
      // ekranda durması "tekrar uygulanabilir" izlenimi verirdi.
      setOnizleme(null);
      setOnay("");
    },
  });

  return (
    <Kart
      baslik="Tek tablo kurtarma"
      aciklama="Bir tabloyu yedekteki hâline döndürür. Tek KAYIT kurtarmadan farkı: yedekten sonra eklenmiş satırlar SİLİNİR."
    >
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-end gap-2">
          <Alan
            etiket="Tablo adı"
            value={tablo}
            onChange={(e) => setTablo(e.target.value)}
            className="w-72"
            yardim="Şemadaki adıyla, örn. Student. Denetim tabloları kurtarılamaz."
          />
          <Buton
            gorunum="ikincil"
            yukleniyor={onizle.isPending}
            disabled={tablo.trim().length < 2}
            onClick={() => onizle.mutate()}
          >
            Önizle
          </Buton>
        </div>

        {onizle.isError && (
          <p role="alert" className="text-hata-500 text-sm font-medium">
            {onizle.error instanceof ApiHatasi ? onizle.error.message : "Önizleme alınamadı."}
          </p>
        )}
        {uygula.isError && (
          <p role="alert" className="text-hata-500 text-sm font-medium">
            {uygula.error instanceof ApiHatasi ? uygula.error.message : "Uygulanamadı."}
          </p>
        )}
        {sonuc && (
          <p role="status" className="rounded-kart bg-notr-50 text-notr-800 px-3 py-2 text-sm">
            {sonuc}
          </p>
        )}

        {onizleme && (
          <div className="rounded-kart border-notr-200 shadow-kart flex flex-col gap-3 border p-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-notr-900 text-sm font-medium">{onizleme.tablo}</span>
              <Rozet ton={RISK_TONU[onizleme.risk]}>{RISK_ADI[onizleme.risk]}</Rozet>
            </div>

            <p className="text-notr-800 text-sm">{onizleme.cumle}</p>

            <dl className="grid grid-cols-3 gap-2 text-sm">
              <div>
                <dt className="text-notr-600">Geri gelecek</dt>
                <dd className="text-notr-900 tabular-nums">{onizleme.ozet.geriGelecek}</dd>
              </div>
              <div>
                <dt className="text-notr-600">Güncellenecek</dt>
                <dd className="text-notr-900 tabular-nums">{onizleme.ozet.guncellenecek}</dd>
              </div>
              <div>
                {/*
                  SİLİNECEK ayrı tonla: tek yıkıcı küme bu. Diğer iki
                  sayıyla aynı görünseydi, kullanıcı üçünü tek bir
                  "etkilenen satır" gibi okurdu.
                */}
                <dt className="text-hata-500 font-medium">Silinecek</dt>
                <dd className="text-hata-500 font-medium tabular-nums">
                  {onizleme.ozet.silinecek}
                </dd>
              </div>
            </dl>

            {onizleme.kirilanBaglantilar.length > 0 && (
              <div className="rounded-kart shadow-kart border-hata-500 bg-hata-500/5 border p-3">
                <p className="text-hata-500 text-sm font-medium">
                  Bu işlem başka tabloların satırlarını sahipsiz bırakır:
                </p>
                <ul className="text-notr-800 mt-1 flex flex-col gap-0.5 text-sm">
                  {onizleme.kirilanBaglantilar.map((k) => (
                    <li key={`${k.tablo}.${k.sutun}`}>
                      {k.tablo}.{k.sutun} — {k.satir} satır
                    </li>
                  ))}
                </ul>
                <p className="text-notr-700 mt-2 text-sm">
                  Doğru yol tek tek KAYIT kurtarma. O satırlar bu ekranda görünmüyor ve sahibi başka
                  biri olabilir.
                </p>
              </div>
            )}

            {onizleme.calistirilabilir ? (
              <>
                {/*
                  TABLO ADINI ELLE YAZDIRIYORUZ. Onay kutusu bir refleks
                  hâline gelir; adı yazmak, hangi tabloyu geri
                  döndürdüğünü okumayı zorunlu kılıyor.
                */}
                <Alan
                  etiket={`Onaylamak için tablo adını yazın: ${onizleme.tablo}`}
                  value={onay}
                  onChange={(e) => setOnay(e.target.value)}
                  yardim="Bu işlem geri alınamaz. Silme ve yazma aynı işlemde yapılır."
                />
                <div>
                  <Buton
                    gorunum="tehlike"
                    yukleniyor={uygula.isPending}
                    disabled={onay.trim() !== onizleme.tablo}
                    onClick={() => uygula.mutate()}
                  >
                    Tabloyu yedekteki hâline döndür
                  </Buton>
                </div>
              </>
            ) : (
              <p className="text-notr-700 text-sm font-medium">
                Bu işlem çalıştırılamaz. Yukarıdaki sebep giderilmeden tablo geri döndürülemez.
              </p>
            )}
          </div>
        )}
      </div>
    </Kart>
  );
}
